import _ from 'underscore';
import { Message, Activity } from 'botframework-directlinejs';

import { BotActivity } from './botTypes';

/**
 * Used by the UI to signal user intent to the AI. Returned by AI as the Name property of plugin results.
 * @see https://msasg.visualstudio.com/Bing_Ads/_git/AdsAppsMT?path=/private/Campaign/MT/Source/AdsCopilot/AdsCopilotMT.Library/Model/PluginScenarios.cs
 */
export enum PluginScenarios {
  Summarize = 'Summarize',
  AccountFluctuation = 'AccountFluctuation',
  CampaignFluctuation = 'CampaignFluctuation',
  PerformanceTimeSeries = 'PerformanceTimeSeries',
}

export const PluginScenarioTroubleshootingDetection = 'AdDeliveryTroubleShooter';

/**
 * Returned by the bot in value.Features array to signal which plugin functions were invoked.
 */
export enum PluginFunctions {
  SummarizeCampaignPerformance = 'SummarizeCampaignPerformance',
  GetAccountFluctuations = 'GetAccountFluctuations',
  GetCampaignFluctuations = 'GetCampaignFluctuations',
  CampaignPerformanceTimeSeries = 'CampaignPerformanceTimeSeries',
  GetAdvertiserAccountDetails = 'GetAdvertiserAccountDetails',
}

export enum IntentType {
  None = 'None',
  ConnectAgent = 'ConnectAgent',
  AdsPreview = 'AdsPreview',
  SignUpLink = 'SignUpLink',
}

export enum AgentQueue {
  Support = 'Support',
  Sales = 'Sales',
  None = 'None',
}

export const CATEGORY_TROUBLESHOOTING = 'troubleshooting';
export const TROUBLESHOOTING_AD_DELIVERY = 'AdDelivery';

export const EVENT_NAME_ENGAGE_TROUBLESHOOTER = 'engageTroubleshooter';
export const EVENT_NAME_PLUGIN = 'pluginResults';
export const EVENT_NAME_LIVE_AGENT_CHAT = 'liveAgentChat';

export const isTypingMessage = (activity: BotActivity & Message) => activity?.text === 'StaticMessages' && (activity as BotActivity).value === 'Typing';

// 1. engage troubleshooting event
// 2. ad delivery plugin event from MT side
export function isTroubleshootingEvent(activity: Activity) {
  const isEngageEvent = activity.type === 'event'
    && activity.name === EVENT_NAME_ENGAGE_TROUBLESHOOTER;

  const isAdDeliveryPluginEvent = activity.type === 'event'
    && activity.name === EVENT_NAME_PLUGIN
    && _.any((activity as any).value, (pluginResult) => pluginResult.Name === PluginScenarioTroubleshootingDetection);

  return isEngageEvent || isAdDeliveryPluginEvent;
}

export const getIsFluctuationsFeature = (activity: any) => {
  const features = (activity.value as any)?.Features;

  return _.any(features, (feature) => (feature === PluginFunctions.GetAccountFluctuations
    || feature === PluginFunctions.GetCampaignFluctuations));
};

export const getIsAdvertiserAccountDetailsFeature = (activity: any) => {
  const features = (activity.value as any)?.Features;

  return _.any(features, (feature) => (feature === PluginFunctions.GetAdvertiserAccountDetails));
};

export const shouldFeatureShowDisclaimer = (activity: any) => {
  const featuresWithDisclaimer = [PluginFunctions.GetAdvertiserAccountDetails, PluginFunctions.GetAccountFluctuations, PluginFunctions.GetCampaignFluctuations];
  const activityFeatures = (activity.value as any)?.Features;

  return !_.isEmpty(_.intersection(featuresWithDisclaimer, activityFeatures));
};

export const getDisclaimerString = (activity: any) => {
  const features = (activity.value as any)?.Features;
  if (_.any(features, (feature) => (feature === PluginFunctions.GetAdvertiserAccountDetails))) {
    return _TL_('Copilot answers questions using Microsoft Advertising\'s data for your account.');
  }

  return _TL_('Copilot answers questions about performance using Microsoft Advertising\'s reporting data for both your enabled and paused campaigns.');
};
